import { BaseGameController } from '@/components/game/implementations/_base/_domain/controller/game-controller';
import { injectable, inject } from 'inversify';
import { IServerServiceFactory } from '@/app-modules/server-service';
import { GameIdentity, GameState, IGameRepresentation, IGameConfiguration, IGame, IGameStageWinnerSet } from '@/components/game/_domain/models';
import { IWinnersTrackerService, ITrackedWinner } from '@/components/game/_domain/modules/winners-tracker-service';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { IConfigurationService } from '@/app-modules/config';
import { IErrorService } from '@/app-modules/error';
import { CatchUpData, ResetData, CallsData } from '@/components/game/_domain';
import { HexPrizeType, getPrizeTypeName } from '../models';
import { PrizeWinnerData } from '@/components/game/_domain/server/prize-winner/prize-winner-data';

interface IHexGameRepresentation extends IGameRepresentation {
  colours?: string[];
}

class HexGame implements IGame {
  public constructor(public readonly gameIdentity: GameIdentity, public readonly gameConfiguration: IGameConfiguration) {
    this.connectionDebugInfo = 'Disconnected';
    this.state = GameState.InPlay;
  }

  public connectionDebugInfo: string;
  public state: GameState;
  public representation?: IHexGameRepresentation;

  public updateRepresentation(newGameRepresentation: Partial<IHexGameRepresentation>): void {
    this.representation = Object.assign({}, this.representation, newGameRepresentation || {});
  }
}

@injectable()
class HexGameController extends BaseGameController {
  protected game: HexGame;

  public constructor(
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) configurationService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IServerServiceFactory) serverServiceFactory: IServerServiceFactory,
    @inject(SERVICE_IDENTIFIERS.IWinnersTrackerService) winnersTrackerService: IWinnersTrackerService,
    @inject(SERVICE_IDENTIFIERS.IErrorService) errorService: IErrorService
  ) {
    super(configurationService, serverServiceFactory, winnersTrackerService, errorService);
  }

  public initialise(gameIdentity: GameIdentity) {
    this.game = this.getInitialGame(gameIdentity);
    this.initialiseGameServerConnection();
    this.initialiseGameRetryPolicySettings();
    this.initialiseGameServerService();
  }

  protected subscribeToGameEvents(): void {
    this.serverSubscriptionStore.push(
      this.gameServerService.subscribeCatchUp(this.handleCatchUpMessage.bind(this)),
      this.gameServerService.subscribeWinners(this.handleWinnersMessage.bind(this)),
      this.gameServerService.subscribeReset(this.handleResetMessage.bind(this)),
      this.gameServerService.subscribeCalls(this.handleCallsMessage.bind(this)),
      this.gameServerService.subscribeNextGame(this.handleNextGameMessage.bind(this)),
    );
  }

  protected handleCatchUpMessage(payload: CatchUpData): void {
    this.game.representation.callboard.update(payload.callValues);
    this.changeGameRepresentation({
      gameId: payload.gameNo,
      numberOfPlayers: payload.numberOfPlayers,
      isLinkedGame: payload.isLinkedGame,
      prizeStructure: payload.prizeStructure,
      colours: payload.colours
    });
  }

  protected handleResetMessage(payload: ResetData): void {
    this.game.representation.callboard.reset();
    this.changeGameState(GameState.InPlay);
    this.changeGameRepresentation({
      gameId: payload.gameId,
      numberOfPlayers: payload.numberOfPlayers,
      promoType: payload.promoType,
      promoMultiplier: payload.promoMultiplier,
      nextGame: null,
      isJackpotWon: null,
      prizeStructure: payload.prizeStructure,
      winners: null,
      colours: []
    });
    this.notifyPlaySoundRequest();
  }

  protected handleCallsMessage(payload: CallsData): void {
    this.game.representation.callboard.update([payload.callValue], payload.callNumber);
    this.game.representation.colours = this.game.representation.colours || [];
    this.game.representation.colours.push(payload.colour.toString());
    this.notifyGameRepresentationChanged();
  }

  protected trackPrizeWinners(winnerSets: IGameStageWinnerSet[]): void {
    const gameIdentity = this.game.gameIdentity;
    winnerSets.filter((winnerSet) => winnerSet.prizeName !== getPrizeTypeName(HexPrizeType.Stakeback)).forEach((winnerSet) => {
      const prizeName = winnerSet.prizeName;
      const trackedWinners = (winnerSet.winners || []).map<ITrackedWinner>((winner) => ({
        gameName: gameIdentity.name,
        clubName: gameIdentity.clubName,
        username: winner.username,
        prizeInPence: winner.prizeInPence,
        prizeName
      }));
      this.winnersTrackerService.trackWinners(...trackedWinners);
    });
  }

  protected changeGameRepresentation(newGameRepresentation: Partial<IHexGameRepresentation>): void {
    this.game.updateRepresentation(newGameRepresentation);
    this.notifyGameRepresentationChanged();
  }
}

export { HexGameController, IHexGameRepresentation };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/implementations/uk/hex/_domain/controller/game-controller.ts